// src/features/coachingAction/coachingActionPlanSlice.ts

import { apiSlice } from '../../api/apiSlice';
import * as constants from '../../constants';

export const coachingActionPlanSlice: any = apiSlice.injectEndpoints({
    endpoints: (builder: any) => ({
        getAnalysisTypes: builder.query({
            query: () => ({
                url: constants.GET_COACHING_ACTION_PLAN_ANALYSIS_TYPES,
            }),
        }),
        getCoachingHistory: builder.query({
            query: (coachingActionPlanId: number) => ({
                url: `${constants.GET_COACHING_HISTORY}/${coachingActionPlanId}`,
            }),
            providesTags: ['CoachingActionPlan'],
        }),
        saveAnalysisNotes: builder.mutation({
            query: (body: { coaching_action_plan_session_id: number; analysis_notes: string }) => ({
                url: constants.SAVE_COACHING_ANALYSIS_NOTES,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CoachingActionSession'],
        }),
        getSessionById: builder.query({
            query: (sessionId: number) => ({
                url: `${constants.GET_SESSION_BY_ID}/${sessionId}`,
            }),
            providesTags: ['CoachingActionSession'],
            invalidatesTags: ['CoachingActionSession'],
        }),
        getPrepareAssociates: builder.query({
            query: ({
                teamLeadEmployeeNumber,
                scorecardVersionId,
            }: {
                teamLeadEmployeeNumber: string;
                scorecardVersionId: number;
            }) => ({
                url: `${constants.GET_PREPARE_ASSOCIATES}?teamLeadEmployeeNumber=${teamLeadEmployeeNumber}&scorecardVersionId=${scorecardVersionId}`,
            }),
            providesTags: ['PrepareAssociates'],
        }),
        getPrepareAssociateMetrics: builder.query({
            query: (coachingNextBestActionId: number) => ({
                url: `${constants.GET_PREPARE_ASSOCIATE_METRICS}?coachingNextBestActionId=${coachingNextBestActionId}`,
            }),
            // Response shape: { statusCode, message, data: { metrics: PrepareAssociateMetric[] } }
            // session_count, latest_session_id, coaching_action_plan_id are per-metric fields.
            providesTags: ['PrepareAssociateMetrics'],
        }),
        getCAPsession: builder.query({
            query: (arg: any) => {
                const { coachingActionPlanId } = arg;
                return {
                    url: constants.GET_CAP_SESSION,
                    params: { coachingActionPlanId },
                };
            },
            providesTags: ['CoachingActionPlan'],
        }),
        followupPeriodResult: builder.query({
            query: (arg: any) => {
                const { employeeNumber, scorecardVersionId, metricId, fromDate, toDate } = arg;
                return {
                    url: constants.GET_FOLLOWUP_PERIOD_RESULT,
                    params: { employeeNumber, scorecardVersionId, metricId, fromDate, toDate },
                };
            },
            providesTags: ['CoachingActionPlan'],
        }),
        updateSessionReview: builder.mutation({
            query: (body: {
                coaching_action_plan_session_id: number;
                frame_notes?: string;
                understand_notes?: string;
                explore_notes?: string;
                coaching_skill_id?: number;
                coaching_behavior_id?: number;
                coaching_action_id?: number;
                action_description?: string;
                success_criteria?: string;
                notes?: string;
                performance_commitment?: number;
                analysis_notes?: string;
                analysis_types?: number[];
            }) => ({
                url: constants.UPDATE_SESSION_REVIEW,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['CoachingActionPlan'],
        }),
        insertCoachingSession: builder.mutation({
            query: (arg: any) => ({
                url: constants.INSERT_COACHING_SESSION,
                method: 'POST',
                body: arg,
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            }),
            invalidatesTags: ['CoachingActionPlan'],
        }),
        getSessionAnalysisTypes: builder.query({
            query: (sessionId: number) => ({
                url: `${constants.GET_SESSION_ANALYSIS_TYPES}/${sessionId}/analysis-types`,
            }),
            providesTags: (_result: any, _err: any, sessionId: number) => [
                { type: 'CoachingActionSession', id: sessionId },
            ],
        }),

        saveAnalysisTypes: builder.mutation({
            query: (body: {
                coachingActionPlanSessionId: number;
                analysisTypeIds: number[];
            }) => ({
                url: constants.SAVE_SESSION_ANALYSIS_TYPES,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CoachingActionSession'],
        }),
        linkCorrectiveActionToCap: builder.mutation({
            query: (body: {
                coaching_action_plan_id: number;
                corrective_action_id: number;
            }) => ({
                url: constants.LINK_CORRECTIVE_ACTION_TO_CAP,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['CoachingActionPlan'],
        }),
        getCapDailyMetricSummary: builder.query({
            query: (arg: {
                employeeNumber: number;
                scorecardVersionId: number;
                scorecardRoleId?: number;
                startDate?: string | null;
                endDate?: string | null;
            }) => ({
                url: constants.GET_CAP_DAILY_METRIC_SUMMARY,
                params: {
                    employeeNumber: arg.employeeNumber,
                    scorecardVersionId: arg.scorecardVersionId,
                    scorecardRoleId: arg.scorecardRoleId ?? 1,
                    startDate: arg.startDate ?? undefined,
                    endDate: arg.endDate ?? undefined,
                },
            }),
        }),
        acknowledgeActionPlan: builder.mutation({
                    query: (arg: { coachingActionPlanId: number; acknowledged: boolean; tlOnCameraResponse: string }) => {
                        return {
                            url: constants.ACKNOWLEDGE_ACTION_PLAN,
                            method: 'PUT',
                            body: arg,
                            headers: {
                                'Content-type': 'application/json; charset=UTF-8',
                            },
                        };
                    },
                    invalidatesTags: ['CoachingActionPlan'],
                }),
        calculateNextBestActionAssociate: builder.mutation({
            query: (arg: { scorecardVersionId: number; employeeNumber: string }) => ({
                url: `${constants.CALCULATE_NEXT_BEST_ACTION_ASSOCIATE}?scorecardVersionId=${arg.scorecardVersionId}&employeeNumber=${arg.employeeNumber}`,
                method: 'POST',
            }),
            invalidatesTags: ['PrepareAssociates', 'PrepareAssociateMetrics'],
        }),
        closeCoachingActionPlan: builder.mutation({
            query: (coachingActionPlanId: number) => ({
                url: `${constants.CLOSE_COACHING_ACTION_PLAN}/${coachingActionPlanId}`,
                method: 'PUT',
            }),
            invalidatesTags: ['CoachingActionPlan', 'PrepareAssociates'],
        }),
        getScorecardVersionsBySupervisor: builder.query({
            query: (supervisorNumber: string) => ({
                url: `${constants.GET_SCORECARD_VERSIONS_BY_SUPERVISOR}?supervisorNumber=${supervisorNumber}`,
            }),
        }),
        updateNbaActioned: builder.mutation({
            query: (body: { coachingNextBestActionId: number; scorecardMetricId: number }) => ({
                url: constants.UPDATE_NBA_ACTIONED,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PrepareAssociates'],
        }),
    }),
});
export const {
    useGetAnalysisTypesQuery,
    useGetCoachingHistoryQuery,
    useGetSessionByIdQuery,
    useSaveAnalysisNotesMutation,
    useGetPrepareAssociatesQuery,
    useGetPrepareAssociateMetricsQuery,
    useGetCAPsessionQuery,
    useFollowupPeriodResultQuery,
    useUpdateSessionReviewMutation,
    useInsertCoachingSessionMutation,
    useLinkCorrectiveActionToCapMutation,
    useGetCapDailyMetricSummaryQuery,
    useGetSessionAnalysisTypesQuery,
    useAcknowledgeActionPlanMutation,
    useSaveAnalysisTypesMutation, 
    useGetScorecardVersionsBySupervisorQuery,
    useCloseCoachingActionPlanMutation,
    useCalculateNextBestActionAssociateMutation,
    useUpdateNbaActionedMutation,
} = coachingActionPlanSlice;
